import { jwtDecode } from 'jwt-decode';

export interface JwtPayload {
    exp?: number; // Expiration time (seconds since Unix epoch)
    iat?: number; // Issued at time (seconds since Unix epoch)
    sub?: string; // Subject (user ID)
    aud?: string; // Audience
    iss?: string; // Issuer
    [key: string]: any; // Allow other claims
}

/**
 * Decodes a JWT token and returns its payload
 * @param token - The JWT token to decode
 * @returns The decoded payload or null if invalid
 */
export const decodeJwtToken = (token: string): JwtPayload | null => {
    try {
        return jwtDecode<JwtPayload>(token);
    } catch (error) {
        console.error('Failed to decode JWT token:', error);
        return null;
    }
};

/**
 * Checks if a JWT token is expired
 * @param token - The JWT token to check
 * @param bufferSeconds - Buffer time in seconds to consider token expired early (default: 30)
 * @returns true if token is expired or will expire within buffer time
 */
export const isTokenExpired = (
    token: string,
    bufferSeconds: number = 30
): boolean => {
    const payload = decodeJwtToken(token);
    if (!payload || !payload.exp) {
        return true; // Consider invalid tokens as expired
    }

    const currentTime = Math.floor(Date.now() / 1000);
    const expirationTime = payload.exp - bufferSeconds;

    return currentTime >= expirationTime;
};

/**
 * Gets the remaining time until token expiration
 * @param token - The JWT token to check
 * @returns Remaining time in seconds, or 0 if expired/invalid
 */
export const getTokenRemainingTime = (token: string): number => {
    const payload = decodeJwtToken(token);
    if (!payload || !payload.exp) {
        return 0;
    }

    const currentTime = Math.floor(Date.now() / 1000);
    const remainingTime = payload.exp - currentTime;

    return Math.max(0, remainingTime);
};

/**
 * Formats remaining time as a human-readable string
 * @param seconds - Time in seconds
 * @returns Formatted string like "5m 30s" or "expired"
 */
export const formatRemainingTime = (seconds: number): string => {
    if (seconds <= 0) {
        return 'expired';
    }

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    if (minutes > 0) {
        return `${minutes}m ${remainingSeconds}s`;
    }

    return `${remainingSeconds}s`;
};

/**
 * Gets token information for debugging
 * @param token - The JWT token to analyze
 * @returns Object with token information
 */
export const getTokenInfo = (token: string) => {
    const payload = decodeJwtToken(token);
    if (!payload) {
        return {
            valid: false,
            expired: true,
            remainingTime: 0,
            formattedTime: 'invalid token',
        };
    }

    const remainingTime = getTokenRemainingTime(token);
    const expired = isTokenExpired(token);

    return {
        valid: true,
        expired,
        remainingTime,
        formattedTime: formatRemainingTime(remainingTime),
        issuedAt: payload.iat
            ? new Date(payload.iat * 1000).toISOString()
            : null,
        expiresAt: payload.exp
            ? new Date(payload.exp * 1000).toISOString()
            : null,
        subject: payload.sub,
        payload,
    };
};

/**
 * Logs token information to console for debugging
 * @param token - The JWT token to log
 * @param label - Optional label for the log
 */
export const logTokenInfo = (token: string, label: string = 'Token') => {
    const info = getTokenInfo(token);
    console.log(`${label} Info:`, {
        valid: info.valid,
        expired: info.expired,
        remainingTime: info.formattedTime,
        issuedAt: info.issuedAt,
        expiresAt: info.expiresAt,
        subject: info.subject,
    });
};

/**
 * Checks if a token should be refreshed proactively
 * @param token - The JWT token to check
 * @param refreshThresholdMinutes - Minutes before expiration to trigger refresh (default: 5)
 * @returns true if token should be refreshed
 */
export const shouldRefreshToken = (
    token: string,
    refreshThresholdMinutes: number = 5
): boolean => {
    const remainingTime = getTokenRemainingTime(token);
    const thresholdSeconds = refreshThresholdMinutes * 60;

    return remainingTime > 0 && remainingTime <= thresholdSeconds;
};
